import { v4 } from "uuid";
import ReactJson from "react-json-view";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useMemo } from "react";
import { useProperty } from "../../components/property-provider";
import schema from "./infrastructure/sellingConfiguration.schema.json";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import {
  SellingConfigurationEntity,
  SellingConfigurationSchema,
} from "./domain/sellingConfiguration.entity";
import { SECTION } from "../../lib/constants";
import getTableColumns from "./tableColumns";
import { FormConfiguration } from "../shared/crud/form";
import { CustomJsonEditor } from "modules/engineMultiConfig/components/customJsonEditor";
import {
  createSellingConfiguration,
  deleteSellingConfiguration,
  findSellingConfiguration,
  getSellingConfiguration,
  updateSellingConfiguration,
} from "./infrastructure/sellingConfiguration.api";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import referenceEntity from "./domain/referenceEntity.json";
import { sellingConfigurationMessages, tableHeaderMessages } from "./sellingConfiguration.messages";

const messages = {
  ...defaultCrudMessages,
  ...sellingConfigurationMessages,
  tableHeaders: tableHeaderMessages,
};

const referenceWithUniqueId = { ...referenceEntity, id: v4() };
const formConfiguration: FormConfiguration<SellingConfigurationEntity> = {
  id: {
    default: () => v4(),
    hidden: true,
  },
  active: {
    hidden: true,
  },
  sellingLocations: {
    hidden: true,
  },
  createdAt: {
    hidden: true,
  },
  updatedAt: {
    hidden: true,
  },
  generalSellingActivationRule: {
    hidden: true,
  },
  propertyTicker: {
    hidden: true,
  },
  sellingConfigurationType: {
    hidden: true,
  },
  propertyRecipients: {
    hidden: true,
  },
  notificationChannels: {
    hidden: true,
  },
  sellingTargets: {
    hidden: true,
  },
  discountApplied: {
    hidden: true,
  },
  serialized: {
    fieldWidget: props => (
      <CustomJsonEditor
        {...props}
        helperReferenceTitle={<FormattedMessage {...messages.jsonReference} />}
        helperReference={
          <ReactJson src={referenceWithUniqueId} collapsed={1} theme="shapeshifter:inverted" />
        }
      />
    ),
    transform: (value: string) => JSON.parse(value),
    messages: {
      label: messages.sellingConfigurationJsonLabel,
    },
  },
};

function SellingConfigurationForm() {
  const intl = useIntl();
  const config = useConfig();
  const httpClient = useHttpClient(config.sellingApiEndpoint);
  const propertyInfo = useProperty();

  const tableColumns = useMemo(() => getTableColumns(intl), [intl]);

  const validate = useCallback(async (entity: SellingConfigurationEntity) => {
    SellingConfigurationSchema.parse(entity.serialized);
  }, []);

  const handleGetAll = useCallback(async () => {
    return findSellingConfiguration(httpClient, propertyInfo.ticker);
  }, [httpClient, propertyInfo.ticker]);

  const handleCreate = useCallback(
    async (entity: SellingConfigurationEntity) => {
      await validate(entity);
      await createSellingConfiguration(httpClient, entity);
      return "success";
    },
    [httpClient, validate]
  );

  const handleGetOne = useCallback(
    async (id: string) => {
      return getSellingConfiguration(httpClient, id);
    },
    [httpClient]
  );

  const handleUpdate = useCallback(
    async (entity: SellingConfigurationEntity) => {
      await validate(entity);
      await updateSellingConfiguration(httpClient, entity.id, entity);
    },
    [httpClient, validate]
  );

  const handleDelete = useCallback(
    (id: string) => {
      return deleteSellingConfiguration(httpClient, id);
    },
    [httpClient]
  );

  return (
    <SimpleCrud<SellingConfigurationEntity>
      managerName={"sellingConfiguration"}
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      listHeight={window.innerHeight - 250}
      section={SECTION.SELLING_CONFIGURATION}
      onGetAll={handleGetAll}
      onGetOne={handleGetOne}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onCreate={handleCreate}
      hideExportButton={true}
      messages={messages}
    />
  );
}

export default withAuthorizationVerifier(SellingConfigurationForm);
